import { z } from 'zod';
import { CrewClaimTypeEnum } from '../../enums/crew.js';
import { enumToZodLiteralUnion, helperSchema } from '../../zodExtensions.js';
import { claimAddress, orderAddress } from './address.js';
import { appliedCustomField, appliedCustomFieldCreate, customUiCustomField, } from './customField.js';
import { storeOrderLineItem } from './lineItem.js';
import { registrationProduct, registrationProductVariant, } from './storeProduct.js';
import { timeline } from './timeline.js';
const matchPhaseSchema = z.enum([
    'exact_sku',
    'exact_barcode',
    'exact_metafield',
    'unmatched',
    'ai_match',
    'exact_name_options',
    'open_search',
]);
const confidenceSchema = z.enum(['high', 'medium', 'low']);
const variantSchema = registrationProduct.shape.variants.element.extend({
    matchedWith: matchPhaseSchema.optional(),
    confidence: confidenceSchema.optional(),
});
const extractedProductDataSchema = z.object({
    name: z.string(),
    quantity: z.number(),
    sku: z.string().nullable(),
    barcode: z.string().nullish(),
    options: z.array(z.object({ name: z.string(), value: z.string() })),
    price: z.number().nullable(),
    currencyCode: z.string().nullable(),
    vendor: z.string().nullish(),
    productType: z.string().nullish(),
    keywordsAboutProduct: z.array(z.string()).optional(),
    confidence: confidenceSchema.optional(),
    bundleComponents: z.array(z.string()).nullish(),
});
const productMatchMetadata = z
    .object({
    extractedProductCount: z.number().optional(),
    candidateCount: z.number().optional(),
    candidatesFitInContext: z.boolean().optional(),
    priceFilteredCount: z.number().optional(),
    suggestedVariants: z.array(variantSchema).optional(),
    possibleVariants: z.array(variantSchema).optional(),
})
    .optional();
export const suggestedVariantsResponse = productMatchMetadata;
export const suggestedRegistration = z.object({
    wasExtractionSuccessful: z.boolean().optional(),
    message: z.string().optional(),
    referrer: helperSchema.optionalizedString,
    productMatchMetadata: productMatchMetadata.optional(),
    metadata: z
        .object({
        extractedProductData: z.array(extractedProductDataSchema).optional(),
        rawExtraction: z.unknown().optional(),
        documentClassification: z.array(z.string()).optional(),
        fileMetadata: z
            .array(z.object({
            type: z.string(),
        }))
            .optional(),
        extractionModel: z.string().optional(),
        tokenUsage: z
            .object({
            prompt: z.number(),
            completion: z.number(),
        })
            .optional(),
        extractionLatencyMs: z.number().optional(),
    })
        .optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    estimatedPurchaseDate: z.string().optional(),
    orderNumber: z.string().optional(),
    email: z.string().optional(),
    address: orderAddress.optional(),
    storeSalesChannel: z
        .object({
        id: z.number(),
        name: z.string(),
    })
        .optional(),
    customFields: z
        .array(appliedCustomField.pick({ id: true, value: true }))
        .optional(),
});
export const registration = z.object({
    id: z.number(),
    externalId: z.string(),
    customerProvidedOrderNumber: z.string().optional(),
    createdOn: z.string(),
    customFields: z.array(appliedCustomField).optional(),
    estimatedPurchaseDate: z.string().nullish(),
    registrationChannelName: z.string(),
    proofOfPurchaseFileUrls: z.array(z.string()),
    emailMarketingConsentCollectedOn: z.string().optional(),
    customerIdFromPlatform: z.string().optional(),
    claims: z.array(z.object({
        id: z.number(),
        externalId: z.string(),
        claimType: enumToZodLiteralUnion(CrewClaimTypeEnum),
    })),
    firstName: z.string(),
    lastName: z.string(),
    suggestedRegistration: suggestedRegistration.nullish(),
    wasCustomerCreatedOnPlatform: z.boolean().optional(),
    wasEmailMarketingConsentUpdatedOnPlatform: z.boolean().optional(),
    // ! properties that live on the order
    orderIdFromPlatform: z.string(),
    address: claimAddress.optional(),
    lineItems: z.array(storeOrderLineItem),
    email: z.string(),
    orderNumber: z.string().optional(),
    referrer: helperSchema.optionalizedString,
    canceledOn: z.string().optional(),
    kobjectIdFromPlatform: z.string().optional(),
    storeSalesChannelId: z.number().optional(),
    salesChannelCode: z.string().optional(),
    discountIncentive: z
        .object({
        code: z.string(),
        message: z.string().optional(),
        redemptionUrl: z.string(),
        expiresOn: z.string().optional(),
        redeemedOn: z.string().optional(),
        orderTotal: z.number().optional(),
        orderNo: z.string().optional(),
        orderCreatedOn: z.string().optional(),
        platformAdminUrl: z.string().optional(),
    })
        .optional(),
    timeline: timeline.optional(),
});
export const registrationUpdate = registration
    .pick({
    estimatedPurchaseDate: true,
    address: true,
})
    .merge(z.object({
    storeProductVariants: z
        .array(z.object({
        idFromPlatform: z.string(),
        quantity: z.number(),
    }))
        .optional(),
    lineItemIdToRemove: z.number().optional(),
    storeSalesChannelId: z.number().optional(),
}));
export const registrationListItem = registration
    .pick({
    registrationChannelName: true,
    createdOn: true,
    externalId: true,
    id: true,
    canceledOn: true,
})
    .merge(z.object({
    customerEmail: z.string(),
    customerName: z.string().nullable(),
    tags: z.array(z.string()),
    storeId: z.number(),
    storeName: z.string(),
}));
export const registrationList = z.array(registrationListItem);
export const registrationCreate = registration
    .omit({
    claims: true,
    externalId: true,
    orderIdFromPlatform: true,
    createdOn: true,
    registrationChannelName: true,
    proofOfPurchaseFileUrls: true,
    estimatedPurchaseDate: true,
    id: true,
    lineItems: true,
    customFields: true,
})
    .merge(z.object({
    firstName: z.string(),
    lastName: z.string(),
    storeProductVariants: z.array(registrationProductVariant
        .pick({
        id: true,
    })
        .merge(z.object({ quantity: z.number() }))),
    storeSalesChannelId: z.number(),
    estimatedPurchaseDate: z.string(),
    proofOfPurchaseFilenames: z.array(z.string()).optional(),
    customFields: z.array(appliedCustomFieldCreate),
}));
export const registrationAnalysisProductRequest = z.object({
    storeSalesChannelName: z.string(),
    estimatedPurchaseDate: z.string(),
    extractedProductData: z.array(extractedProductDataSchema),
});
export const registrationAnalysisProductResponse = z.array(variantSchema);
export const registrationAnalysisRequest = z.object({
    proofOfPurchaseFiles: z.array(z.object({
        src: z.string(),
        type: z.string(),
    })),
    shouldCollectAddress: z.boolean(),
    offeredCustomFields: z.array(customUiCustomField.pick({
        id: true,
        description: true,
        displayName: true,
    })),
    offeredSalesChannels: z.array(z.object({
        id: z.number(),
        name: z.string(),
    })),
});
//# sourceMappingURL=registration.js.map